import {Button} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import numeral from 'numeral';
import React, {memo, useCallback, useMemo, useState} from 'react';
import {Modal} from 'semantic-ui-react';

import {
  type HasSubsWithSubType,
  type Privileges,
  type SubscriptionForOrg,
} from '../../../util/accounts/pricing';
import {
  bytesInGB,
  freePersonalStorageInGB,
  getEstimatedArtifactReferenceCharge,
  getEstimatedStorageCharge,
  getFreeStorageInGB,
  STORAGE_PRICE_PER_GB_TIER_1,
} from '../../../util/storage';
import * as urls from '../../../util/urls';
import {BillingAddressContextProvider} from './BillingAddressContext';
import {
  NewCheckoutModalContext,
  NewCheckoutModalUpdaterContext,
} from './NewCheckoutModalContext';
import * as S from './SubscribeCheckoutModal.styles';
import {
  PRODUCT_LABEL,
  SubscribeForm,
  type SubscribeProduct,
} from './SubscribeForm';

type SubscribeCheckoutModalProps = {
  onTransactionCompleted: () => void;
  onClose: () => void;
  totalStorageBytes?: number;
  totalTrackingBytes?: number;
  orgID: string;
  stripePaymentMethods?: Array<{}>;
  primarySub: SubscriptionForOrg<HasSubsWithSubType> | null | undefined;
  privileges: Privileges;
  product?: SubscribeProduct;
};

const CHECKOUT_DESCRIPTION = (
  <>
    Keep going beyond your plan allowances.{' '}
    <TargetBlank href="https://wandb.ai/site/pricing/tokens">
      View pricing
    </TargetBlank>
  </>
);

const StoragePricePerGB = () => (
  <S.Price>
    <S.CostPerGB>
      {STORAGE_PRICE_PER_GB_TIER_1.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })}
    </S.CostPerGB>
    <S.Unit>/ GB</S.Unit>
  </S.Price>
);
const BILLING_CAPTION = 'Billed monthly for actual usage. Cancel anytime.';

const SUCCESS_BODY: Record<SubscribeProduct, string> = {
  storage:
    'Thanks for subscribing to a storage plan to unlock additional space for your ML projects. Feel free to contact us with questions and feedback.',
  inference:
    'Thanks for subscribing to an inference plan. You can now use our inference services with flexible pricing. Feel free to contact us with questions and feedback.',
  sandboxes:
    "Thanks for activating pay-as-you-go for sandboxes. You'll only be billed for usage beyond your plan allowance. Feel free to contact us with questions and feedback.",
};

const SubscribeCheckoutModalComp = (props: SubscribeCheckoutModalProps) => {
  const {onClose} = props;
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const contextValue = useMemo(() => ({submitting}), [submitting]);
  const updaterContextValue = useMemo(() => ({setSubmitting}), []);

  const onCloseWhenNotSubmitting = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [submitting, onClose]);

  return (
    <NewCheckoutModalContext.Provider value={contextValue}>
      <NewCheckoutModalUpdaterContext.Provider value={updaterContextValue}>
        <Modal
          open
          basic
          className="checkout-modal"
          onClose={onCloseWhenNotSubmitting}>
          {showSuccess ? (
            <SubscriptionSuccessForm {...props} onClose={onClose} />
          ) : (
            <SubscribeCheckoutModalForm
              {...props}
              onTransactionCompleted={() => {
                setShowSuccess(true);
                props.onTransactionCompleted();
              }}
            />
          )}
        </Modal>
      </NewCheckoutModalUpdaterContext.Provider>
    </NewCheckoutModalContext.Provider>
  );
};
const SubscribeCheckoutModalForm = ({
  totalStorageBytes,
  totalTrackingBytes,
  orgID,
  stripePaymentMethods,
  onTransactionCompleted,
  primarySub,
  privileges,
  product = 'storage',
}: SubscribeCheckoutModalProps) => {
  const estimatedBill = useMemo(() => {
    const storageEstimate = getEstimatedStorageCharge(
      totalStorageBytes ?? 0,
      primarySub,
      privileges
    );
    const artifactEstimate = getEstimatedArtifactReferenceCharge(
      totalTrackingBytes ?? 0
    );
    const totalEstimate = storageEstimate + artifactEstimate;
    return totalEstimate.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }, [totalStorageBytes, totalTrackingBytes, primarySub, privileges]);

  const isStorage = product === 'storage';
  const freeStorageInGB =
    primarySub != null
      ? getFreeStorageInGB(primarySub, privileges)
      : freePersonalStorageInGB;
  const storageLimitString = numeral(freeStorageInGB * bytesInGB).format('0 b');

  return (
    <S.Container>
      <S.MainContainer>
        <S.PlanOverviewSection>
          <S.HeaderSection>
            <S.Title>
              Activate pay-as-you-go for {PRODUCT_LABEL[product]}
            </S.Title>
            {isStorage && <StoragePricePerGB />}
          </S.HeaderSection>
          <S.Description>
            {isStorage
              ? `Your first ${storageLimitString} of storage are free, after that you only pay for what you use.`
              : CHECKOUT_DESCRIPTION}
          </S.Description>

          <S.TotalAmountSection>
            <S.DueToday>Due today</S.DueToday>
            <S.DueToday>{estimatedBill}</S.DueToday>
          </S.TotalAmountSection>

          <S.DescriptionItalic>{BILLING_CAPTION}</S.DescriptionItalic>
        </S.PlanOverviewSection>
      </S.MainContainer>
      <S.SideContainer>
        <BillingAddressContextProvider>
          <SubscribeForm
            organizationID={orgID}
            hasStripePaymentMethods={
              stripePaymentMethods != null && stripePaymentMethods.length > 0
            }
            onSubscriptionCompleted={onTransactionCompleted}
            product={product}
          />
        </BillingAddressContextProvider>
      </S.SideContainer>
    </S.Container>
  );
};
type SubscriptionSuccessFormProps = {
  onClose: () => void;
  product?: SubscribeProduct;
};

const SubscriptionSuccessForm: React.FC<SubscriptionSuccessFormProps> =
  React.memo(({onClose, product = 'storage'}) => {
    const label = PRODUCT_LABEL[product];
    return (
      <div className="checkout pricing-panel" style={{width: '406px'}}>
        <S.UsageSubscriptionPopup>
          <S.UsageSubscriptionPopupTitle>
            {label} pay-as-you-go activated
          </S.UsageSubscriptionPopupTitle>
          <S.UsageSubscriptionPopupContent>
            {SUCCESS_BODY[product]}
          </S.UsageSubscriptionPopupContent>
          <S.ConfirmationButtons>
            <Button
              variant="secondary"
              onPress={() => {
                window.analytics?.track(`${label} plan contact us clicked`);
                // oxlint-disable-next-line wandb/no-unprefixed-urls
                window.open(urls.contact());
              }}>
              Contact us
            </Button>
            <Button
              onPress={() => {
                window.analytics?.track(`${label} confirmation OK clicked`);
                onClose();
              }}>
              OK
            </Button>
          </S.ConfirmationButtons>
        </S.UsageSubscriptionPopup>
      </div>
    );
  });

export const SubscribeCheckoutModal = memo(SubscribeCheckoutModalComp);
